import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useUser } from '../hooks/queries/useAuth';
import { useInitialLoadingScreenManager } from '../hooks/useInitialLoadingScreenManager';
import { supabase } from '../lib/supabase';
import { logger } from '../utils/logger';
import { fireConfetti } from '../lib/confetti';
import { SUBSCRIPTION_PLANS, SUBSCRIPTION_TIERS } from '../types/subscriptions';

type SubscriptionTier = 'FREE' | 'PRO' | 'SMALL_TEAM' | 'MEDIUM_TEAM' | 'LARGE_TEAM' | 'VIP';

interface VerificationStatus {
  success: boolean;
  message?: string;
  tier?: SubscriptionTier;
  priceId?: string;
}

export const SuccessPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { data: user } = useUser();
  const [isVerifying, setIsVerifying] = useState(true);
  const [verificationStatus, setVerificationStatus] = useState<VerificationStatus | null>(null);

  // Loading screen management
  const { markRouteComplete } = useInitialLoadingScreenManager();

  // Fire confetti when subscription is successfully verified
  useEffect(() => {
    if (verificationStatus?.success) {
      fireConfetti();
    }
  }, [verificationStatus?.success]);

  // Mark success page as fully loaded when verification is complete
  // Using a ref to track if we've already marked complete to avoid infinite updates
  const hasMarkedCompleteRef = useRef(false);
  useEffect(() => {
    // Only mark as complete when verification is done
    if (!isVerifying && !hasMarkedCompleteRef.current) {
      hasMarkedCompleteRef.current = true;
      markRouteComplete();
    }
  }, [isVerifying, markRouteComplete]);

  useEffect(() => {
    // Log all params for debugging
    logger.component('SuccessPage: Initial render', { 
      searchParams: Object.fromEntries(searchParams.entries()),
      locationSearch: location.search,
      hasSessionId: !!searchParams.get('session_id'),
      pathname: location.pathname
    });
  }, []);

  useEffect(() => {
    // Prevent the component from triggering redirects while checking the session
    if (!user) {
      logger.component('SuccessPage: No authenticated user yet, waiting');
      return;
    }
    
    const verifyCheckoutSession = async () => {
      try {
        logger.component('SuccessPage: Starting checkout verification');
        const sessionId = searchParams.get('session_id');

        // If no session ID in URL params, check location.search directly
        // Sometimes React Router's useSearchParams doesn't parse correctly after redirects
        if (!sessionId) {
          logger.component('SuccessPage: No session ID in SearchParams, checking location.search');
          const urlParams = new URLSearchParams(location.search);
          const sessionIdFromUrl = urlParams.get('session_id');
          
          if (!sessionIdFromUrl) {
            logger.error('SuccessPage: No session ID found in URL');
            setIsVerifying(false);
            setVerificationStatus({
              success: false,
              message: 'No session ID was provided. Please contact support if this persists.'
            });
            return;
          }
          
          logger.component('SuccessPage: Found session ID in location.search', { sessionId: sessionIdFromUrl });
          
          // Continue with the session ID from URL
          const {
            data: { session },
          } = await supabase.auth.getSession();

          if (!session) {
            logger.error('SuccessPage: No active session available');
            setIsVerifying(false);
            setVerificationStatus({
              success: false,
              message: 'Authentication error. Please try signing in again.'
            });
            return;
          }

          logger.component('SuccessPage: Invoking verify-checkout-session function', { sessionId: sessionIdFromUrl });
          const response = await supabase.functions.invoke('verify-checkout-session', {
            body: JSON.stringify({ sessionId: sessionIdFromUrl }),
            headers: {
              Authorization: `Bearer ${session.access_token}`,
            },
          });

          if (response.error) {
            logger.error('SuccessPage: Checkout verification failed', { error: response.error });
            setIsVerifying(false);
            setVerificationStatus({
              success: false,
              message: response.error.message || 'Checkout verification failed. Please contact support.'
            });
            return;
          }

          // Try to get subscription tier from response
          const subscriptionTier = response.data?.tier as SubscriptionTier;
          const priceId = response.data?.priceId as string;
          const subscriptionInterval = response.data?.interval as string;
          
          if (!subscriptionTier) {
            logger.error('SuccessPage: Missing subscription tier in response', { 
              responseData: response.data
            });
            setIsVerifying(false);
            setVerificationStatus({
              success: false,
              message: 'Subscription was processed but tier information is missing. Please contact support.'
            });
            return;
          }
          
          logger.component('SuccessPage: Verification successful', { 
            tier: subscriptionTier,
            priceId
          });
          
          setIsVerifying(false);
          setVerificationStatus({
            success: true,
            message: 'Your subscription has been successfully activated!',
            tier: subscriptionTier,
            priceId,
            interval: subscriptionInterval
          });
          return;
        }

        // Original flow with sessionId from searchParams
        const {
          data: { session },
        } = await supabase.auth.getSession();

        if (!session) {
          logger.error('SuccessPage: No active session available');
          setIsVerifying(false);
          setVerificationStatus({
            success: false,
            message: 'Authentication error. Please try signing in again.'
          });
          return;
        }

        logger.component('SuccessPage: Invoking verify-checkout-session function', { sessionId });
        const response = await supabase.functions.invoke('verify-checkout-session', {
          body: JSON.stringify({ sessionId }),
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        });

        if (response.error) {
          logger.error('SuccessPage: Checkout verification failed', { error: response.error });
          setIsVerifying(false);
          setVerificationStatus({
            success: false,
            message: response.error.message || 'Checkout verification failed. Please contact support.'
          });
          return;
        }

        // Try to get subscription tier from response
        const subscriptionTier = response.data?.tier as SubscriptionTier;
        const priceId = response.data?.priceId as string;
        const subscriptionInterval = response.data?.interval as string;
        
        if (!subscriptionTier) {
          logger.error('SuccessPage: Missing subscription tier in response', { 
            responseData: response.data
          });
          setIsVerifying(false);
          setVerificationStatus({
            success: false,
            message: 'Subscription was processed but tier information is missing. Please contact support.'
          });
          return;
        }
        
        logger.component('SuccessPage: Verification successful', { 
          tier: subscriptionTier,
          priceId,
          interval: subscriptionInterval
        });
        
        setIsVerifying(false);
        setVerificationStatus({
          success: true,
          message: 'Your subscription has been successfully activated!',
          tier: subscriptionTier,
          priceId,
          interval: subscriptionInterval
        });
      } catch (err) {
        logger.error('SuccessPage: Checkout verification error', { error: err });
        setIsVerifying(false);
        setVerificationStatus({
          success: false,
          message: 'Failed to verify subscription. Please contact support if this persists.'
        });
      }
    };

    if (user) {
      verifyCheckoutSession();
    }
  }, [searchParams, navigate, user, location.search, location]);

  // Get subscription details based on tier
  const getSubscriptionDetails = () => {
    if (!verificationStatus?.tier) {
      return {
        title: 'Subscription Activation Error',
        message: 'Unable to determine subscription details.',
        details: 'Please contact support for assistance.',
        iconClass: 'text-red-500',
        buttonClass: 'bg-red-600 hover:bg-red-700',
        interval: 'unknown'
      };
    }
    
    const tier = verificationStatus.tier;
    const priceId = verificationStatus.priceId;
    const interval = verificationStatus.interval;
    
    // Try to find the exact plan by priceId
    let planDetails = null;
    if (priceId) {
      planDetails = Object.values(SUBSCRIPTION_PLANS).find(plan => plan.stripePriceId === priceId);
      logger.component('SuccessPage: Finding plan by priceId', { 
        priceId, 
        foundPlan: planDetails?.name || 'Not found',
        interval: planDetails?.interval || 'unknown'
      });
    }
    
    // Fallback to finding a plan by tier
    if (!planDetails) {
      const plans = Object.values(SUBSCRIPTION_PLANS).filter(plan => plan.tier === tier);
      logger.component('SuccessPage: Finding plans by tier', { 
        tier, 
        foundCount: plans.length,
        availablePlans: plans.map(p => `${p.name} (${p.interval})`)
      });
      
      if (plans.length > 0) {
        // Don't prefer any interval by default, use the first plan
        planDetails = plans[0];
      }
    }
    
    const tierDetails = SUBSCRIPTION_TIERS[tier];
    
    switch(tier) {
      case 'pro':
        return {
          title: 'Pro Plan Activated!',
          message: 'You now have access to unlimited projects and all Pro features.',
          details: 'Create as many projects as you need with our advanced AI features.',
          iconClass: 'text-blue-500',
          buttonClass: 'bg-blue-600 hover:bg-blue-700',
          maxProjects: tierDetails?.maxProjects || 'unlimited',
          planName: planDetails?.name || 'Pro',
          interval: planDetails?.interval || interval || 'year'
        };
      case 'team_small':
        return {
          title: 'Team Starter Plan Activated!',
          message: 'Your team subscription with up to 5 members is now active.',
          details: 'Invite team members and collaborate on unlimited projects.',
          iconClass: 'text-purple-500',
          buttonClass: 'bg-purple-600 hover:bg-purple-700',
          maxCollaborators: tierDetails?.maxCollaborators || 5,
          planName: planDetails?.name || 'Team Starter',
          interval: planDetails?.interval || interval || 'year'
        };
      case 'team_medium':
        return {
          title: 'Team Growth Plan Activated!',
          message: 'Your team subscription with up to 20 members is now active.',
          details: 'Invite your entire team and collaborate on unlimited projects.',
          iconClass: 'text-purple-500',
          buttonClass: 'bg-purple-600 hover:bg-purple-700',
          maxCollaborators: tierDetails?.maxCollaborators || 20,
          planName: planDetails?.name || 'Team Growth',
          interval: planDetails?.interval || interval || 'year'
        };
      case 'team_large':
        return {
          title: 'Team Gold Plan Activated!',
          message: 'Your team subscription with up to 50 members is now active.',
          details: 'Bring your whole organization and collaborate on unlimited projects.',
          iconClass: 'text-purple-500',
          buttonClass: 'bg-purple-600 hover:bg-purple-700',
          maxCollaborators: tierDetails?.maxCollaborators || 50,
          planName: planDetails?.name || 'Team Gold',
          interval: planDetails?.interval || interval || 'year'
        };
      case 'vip':
        return {
          title: 'VIP Status Activated!',
          message: 'Your VIP subscription is now active with unlimited access.',
          details: 'Enjoy all premium features with unlimited team members.',
          iconClass: 'text-yellow-500',
          buttonClass: 'bg-yellow-600 hover:bg-yellow-700',
          maxCollaborators: 'unlimited',
          planName: 'VIP',
          interval: 'permanent'
        };
      default:
        return {
          title: 'Subscription Activated!',
          message: `Your ${tier} subscription has been successfully activated!`,
          details: 'Thank you for your support.',
          iconClass: 'text-green-500',
          buttonClass: 'bg-blue-600 hover:bg-blue-700',
          planName: planDetails?.name || tier,
          interval: planDetails?.interval || interval || 'unknown'
        };
    }
  };

  if (isVerifying) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-900 text-white">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mb-4"></div>
        <h2 className="text-xl font-semibold">Verifying your subscription...</h2>
        <p className="mt-2 text-gray-400">Please wait while we confirm your payment.</p>
      </div>
    );
  }

  const subscriptionDetails = verificationStatus?.success ? getSubscriptionDetails() : null;

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-900 text-white p-4">
      <div className="max-w-md w-full bg-gray-800 rounded-lg shadow-lg p-8">
        {verificationStatus?.success ? (
          <>
            <div className="flex justify-center mb-6">
              <svg className={`w-16 h-16 ${subscriptionDetails?.iconClass || 'text-green-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-center mb-4">{subscriptionDetails?.title || 'Subscription Activated!'}</h1>
            <p className="text-gray-300 text-center mb-4">{subscriptionDetails?.message || verificationStatus.message}</p>
            <p className="text-gray-400 text-center mb-4">{subscriptionDetails?.details}</p>
            
            <div className="bg-gray-700/50 rounded-lg p-4 mb-8">
              <h3 className="text-lg font-semibold text-center mb-2">Subscription Details</h3>
              <ul className="space-y-2 text-gray-300">
                <li className="flex justify-between">
                  <span>Plan:</span>
                  <span className="font-semibold">{subscriptionDetails?.planName || 'Pro'}</span>
                </li>
                <li className="flex justify-between">
                  <span>Billing:</span>
                  <span className="font-semibold">{subscriptionDetails?.interval === 'month' ? 'Monthly' : subscriptionDetails?.interval === 'year' ? 'Annual' : 'Permanent'}</span>
                </li>
                <li className="flex justify-between">
                  <span>Projects:</span>
                  <span className="font-semibold">{subscriptionDetails?.maxProjects || 'Unlimited'}</span>
                </li>
                {subscriptionDetails?.maxCollaborators && (
                  <li className="flex justify-between">
                    <span>Team members:</span>
                    <span className="font-semibold">{subscriptionDetails.maxCollaborators}</span>
                  </li>
                )}
              </ul>
            </div>
          </>
        ) : (
          <>
            <div className="flex justify-center mb-6">
              <svg className="w-16 h-16 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-center mb-4">Verification Issue</h1>
            <p className="text-gray-300 text-center mb-6">{verificationStatus?.message || 'There was an issue verifying your subscription.'}</p>
            <p className="text-gray-400 text-center mb-8">Please contact our support team for assistance.</p>
          </>
        )}
        
        <div className="flex justify-center">
          <button
            onClick={() => navigate('/project')}
            className={`px-6 py-3 ${subscriptionDetails?.buttonClass || 'bg-blue-600 hover:bg-blue-700'} text-white rounded-md transition-colors`}
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    </div>
  );
};